import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken, setStoredUser, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { TrendingUp, ArrowRight, ArrowLeft, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";

// Two-stage login:
//   stage = "email":     enter email → calls /auth/check-email
//   stage = "setup":     first-time user picks their own password
//   stage = "password":  returning user enters their password
export default function Login() {
  const [stage, setStage] = useState("email");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const checkEmail = async (e) => {
    e?.preventDefault();
    if (!email.trim()) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/check-email", { email: email.trim().toLowerCase() });
      setName(data.name || "");
      setStage(data.needs_setup ? "setup" : "password");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const setupPassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/setup", {
        email: email.trim().toLowerCase(), password,
      });
      setToken(data.token);
      setStoredUser(data.user);
      toast.success("Password saved — welcome to StrydeFinance");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (e) => {
    e.preventDefault();
    if (!password) return toast.error("Enter your password");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(), password,
      });
      setToken(data.token);
      setStoredUser(data.user);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => { setStage("email"); setPassword(""); setConfirm(""); };

  const forgotPassword = async () => {
    if (!window.confirm(
      `Reset password for ${email}?\n\nThis will clear the current password. ` +
      `You'll be asked to pick a new one on the next screen.`
    )) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("Password cleared — pick a new one");
      setPassword("");
      setStage("setup");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] grain-bg">
      <Toaster richColors position="top-right" />
      <div className="w-full max-w-md mx-4">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="relative">
            <TrendingUp className="w-9 h-9" style={{ color: "#0A4C95" }} strokeWidth={2.5} />
            <div className="absolute -right-1 -bottom-1 w-3 h-3 rounded-full" style={{ background: "#F05D23" }} />
          </div>
          <div>
            <div className="font-bold text-2xl tracking-tight text-slate-900">Stryde</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 -mt-0.5">Finance</div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg shadow-sm p-8">
          {stage === "email" && (
            <>
              <h1 className="text-xl font-bold text-slate-900">Sign in</h1>
              <p className="text-sm text-slate-500 mt-1">Use your work email.</p>
              <form onSubmit={checkEmail} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@stryderesearch.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    autoComplete="username"
                    data-testid="login-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#0A4C95] hover:bg-[#083d79]" data-testid="login-continue-btn">
                  {loading ? "Checking..." : "Continue"} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </form>
            </>
          )}

          {stage === "setup" && (
            <>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#0A4C95]" />
                <h1 className="text-xl font-bold text-slate-900">Set your password</h1>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Welcome{name ? `, ${name}` : ""}. This is your first time signing in — pick a password and you're in.
              </p>
              <form onSubmit={setupPassword} className="mt-6 space-y-4">
                <div className="text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded border border-slate-200">
                  Email: <span className="font-semibold text-slate-700">{email}</span>
                </div>
                <div>
                  <Label htmlFor="pw">New password (≥ 8 characters)</Label>
                  <div className="relative">
                    <Input
                      id="pw"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoFocus
                      autoComplete="new-password"
                      data-testid="setup-password-input"
                    />
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input
                    id="confirm"
                    type={showPw ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    data-testid="setup-confirm-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#0A4C95] hover:bg-[#083d79]" data-testid="setup-submit-btn">
                  {loading ? "Saving..." : "Save password & continue"} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button type="button" variant="ghost" onClick={goBack} className="w-full text-slate-500">
                  <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Use a different email
                </Button>
              </form>
            </>
          )}

          {stage === "password" && (
            <>
              <h1 className="text-xl font-bold text-slate-900">Welcome back{name ? `, ${name}` : ""}</h1>
              <p className="text-sm text-slate-500 mt-1">Enter your password to continue.</p>
              <form onSubmit={signIn} className="mt-6 space-y-4">
                <div className="text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded border border-slate-200">
                  Email: <span className="font-semibold text-slate-700">{email}</span>
                </div>
                <div>
                  <Label htmlFor="pw">Password</Label>
                  <div className="relative">
                    <Input
                      id="pw"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoFocus
                      autoComplete="current-password"
                      data-testid="login-password-input"
                    />
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#0A4C95] hover:bg-[#083d79]" data-testid="login-submit-btn">
                  {loading ? "Signing in..." : "Sign in"} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <div className="flex items-center justify-between pt-1">
                  <Button type="button" variant="ghost" onClick={goBack} className="text-slate-500" data-testid="login-back-btn">
                    <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Use a different email
                  </Button>
                  <Button type="button" variant="ghost" onClick={forgotPassword} disabled={loading} className="text-slate-500 hover:text-[#0A4C95]" data-testid="login-forgot-btn">
                    Forgot password?
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>

        <p className="text-[11px] text-slate-400 text-center mt-4">
          Access is restricted to authorized Stryde Research staff.
        </p>
      </div>
    </div>
  );
}
